import { useQuery } from "@tanstack/react-query";
import { Navigate, Outlet, Route, Routes, useLocation, useParams } from "react-router-dom";
import { apiClient, responseData } from "@/api/client";
import type { ReconciliationRun } from "@/api/types";
import { useAuth } from "@/app/auth";
import { WorkspaceProvider, useWorkspace } from "@/app/workspace";
import { Layout } from "@/components/Layout";
import { HomePage } from "@/pages/HomePage";
import { LoginPage } from "@/pages/LoginPage";
import { BanksPage } from "@/pages/BanksPage";
import { CardsPage } from "@/pages/CardsPage";
import { OtaPage } from "@/pages/OtaPage";
import { AuditPage } from "@/pages/AuditPage";
import { SettingsAgenciesPage, SettingsBanksPage, SettingsCardsPage, SettingsHotelsPage, SettingsPage, SettingsUsersPage } from "@/pages/SettingsPage";

function RequireHotel() { const { hotelId } = useWorkspace(); return hotelId ? <Outlet /> : <Navigate to="/" replace />; }
function ImportRouteRedirect({ pathname }: { pathname: string }) {
  const location = useLocation();
  const { id = "" } = useParams();
  const params = new URLSearchParams(location.search);
  const legacyParams = [
    ["page", "importPage"],
    ["status", "importStatus"],
    ["q", "importQ"],
    ["rowPage", "importRowPage"],
  ] as const;
  legacyParams.forEach(([legacy, current]) => {
    const value = params.get(legacy);
    if (value) params.set(current, value);
    params.delete(legacy);
  });
  params.set("modal", "imports");
  if (id) params.set("importId", id);
  else params.delete("importId");
  return <Navigate to={{ pathname, search: params.toString() }} replace />;
}
export function BanksEntryRedirect() {
  const { hotelId, period } = useWorkspace();
  const location = useLocation();
  const latest = useQuery({
    queryKey: ["run", "bank", hotelId, period],
    queryFn: async (): Promise<ReconciliationRun | null> =>
      (await responseData(apiClient.GET("/api/v1/banking/reconciliations/latest", { params: { query: { hotelId, period } } }))) ?? null,
    enabled: Boolean(hotelId && period),
  });

  if (latest.isPending) return <div className="empty-state"><p>Comprobando el período bancario…</p></div>;
  if (latest.isError) return <section className="empty-state" role="alert"><h1>No se pudo abrir Bancos</h1><p>No pudimos comprobar si el período tiene una conciliación.</p><button type="button" className="button button--primary" onClick={() => void latest.refetch()}>Reintentar</button></section>;

  return <Navigate to={{ pathname: latest.data ? "/bancos/revision" : "/bancos/movimientos", search: location.search }} replace />;
}
export function CardsEntryRedirect() {
  const { hotelId, period } = useWorkspace();
  const location = useLocation();
  const latest = useQuery({
    queryKey: ["run", "card", hotelId, period],
    queryFn: async (): Promise<ReconciliationRun | null> =>
      (await responseData(apiClient.GET("/api/v1/cards/reconciliations/latest", { params: { query: { hotelId, period } } }))) ?? null,
    enabled: Boolean(hotelId && period),
  });

  if (latest.isPending) return <div className="empty-state"><p>Comprobando el período de Tarjetas…</p></div>;
  if (latest.isError) return <section className="empty-state" role="alert"><h1>No se pudo abrir Tarjetas</h1><p>No pudimos comprobar si el período tiene una conciliación.</p><button type="button" className="button button--primary" onClick={() => void latest.refetch()}>Reintentar</button></section>;

  return <Navigate to={{ pathname: latest.data ? "/tarjetas/revision" : "/tarjetas/liquidaciones", search: location.search }} replace />;
}
function ProtectedApp() {
  const { user, loading } = useAuth();
  if (loading) return <div className="empty-state"><h1>525 Hotel</h1><p>Preparando la operación…</p></div>;
  if (!user) return <Navigate to="/acceso" replace />;
  return <WorkspaceProvider><Routes><Route element={<Layout />}><Route index element={<HomePage />} />
    <Route element={<RequireHotel />}>
      <Route path="bancos" element={<BanksEntryRedirect />} /><Route path="bancos/conciliar" element={<BanksPage />} /><Route path="bancos/movimientos" element={<BanksPage view="data" />} /><Route path="bancos/cargas" element={<ImportRouteRedirect pathname="/bancos/movimientos" />} /><Route path="bancos/cargas/:id" element={<ImportRouteRedirect pathname="/bancos/movimientos" />} /><Route path="bancos/revision" element={<BanksPage view="review" />} />
      <Route path="tarjetas" element={<CardsEntryRedirect />} /><Route path="tarjetas/conciliar" element={<CardsPage view="reconciliation" />} /><Route path="tarjetas/liquidaciones" element={<CardsPage view="data" />} /><Route path="tarjetas/cargas" element={<ImportRouteRedirect pathname="/tarjetas/liquidaciones" />} /><Route path="tarjetas/cargas/:id" element={<ImportRouteRedirect pathname="/tarjetas/liquidaciones" />} /><Route path="tarjetas/revision" element={<CardsPage view="review" />} />
      <Route path="cobranzas-ota" element={<Navigate to="/cobranzas-ota/reservas" replace />} /><Route path="cobranzas-ota/reservas" element={<OtaPage view="data" />} /><Route path="cobranzas-ota/sincronizaciones" element={<OtaPage view="syncs" />} /><Route path="cobranzas-ota/conciliar" element={<Navigate to="/cobranzas-ota/reservas" replace />} /><Route path="cobranzas-ota/cargas/*" element={<Navigate to="/cobranzas-ota/reservas" replace />} />
    </Route>
    <Route path="configuracion" element={<SettingsPage />} /><Route path="configuracion/usuarios" element={<SettingsUsersPage />} /><Route path="configuracion/hoteles" element={<SettingsHotelsPage />} /><Route path="configuracion/bancos" element={<SettingsBanksPage />} /><Route path="configuracion/tarjetas" element={<SettingsCardsPage />} /><Route path="configuracion/agencias" element={<SettingsAgenciesPage />} /><Route path="auditoria" element={<AuditPage />} />
  </Route></Routes></WorkspaceProvider>;
}
export default function App() { return <Routes><Route path="/acceso" element={<LoginPage />} /><Route path="/*" element={<ProtectedApp />} /></Routes>; }
